import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  WandSparkles, Shuffle, RefreshCw, GitBranch, RotateCcw,
  CheckCircle2, Users, Globe, Briefcase, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Purple AI-generate panel. Reused by both Message Template and Connect Note.
 * Props:
 *   kind: "message" | "connect_note"
 *   audience: string[]     — auto-inferred from URL, editable chips
 *   onGenerated: (text, meta) => void
 *   initialGoal?: string
 *   initialKeywords?: string[]
 *   initialLength?: "short" | "medium" | "long"
 *   initialTone?: "professional" | "friendly" | "direct" | "warm"
 */
// LocalStorage keys — remember last-used goal/keywords/length/tone per template kind
const STORE_KEY = (kind) => `lnmsg_gen_${kind}`;
const loadSaved = (kind) => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY(kind)) || '{}'); } catch { return {}; }
};
const saveSaved = (kind, data) => {
  try { localStorage.setItem(STORE_KEY(kind), JSON.stringify(data)); } catch {}
};

export default function AIGenerateTemplatePanel({
  kind = 'message',
  audience = [],
  onGenerated,
  initialGoal = '',
  initialKeywords = [],
  initialLength = 'medium',
  initialTone = 'professional',
}) {
  const saved = loadSaved(kind);
  const [goal, setGoal] = useState(saved.goal ?? initialGoal);
  const [keywords, setKeywords] = useState(saved.keywords ?? initialKeywords);
  const [kwInput, setKwInput] = useState('');
  const [length, setLength] = useState(saved.length ?? (kind === 'connect_note' ? 'short' : initialLength));
  const [tone, setTone] = useState(saved.tone ?? initialTone);
  const [variants, setVariants] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [lastMeta, setLastMeta] = useState(null);

  // Persist any change so next drawer open pre-fills from last session
  useEffect(() => { saveSaved(kind, { goal, keywords, length, tone }); }, [kind, goal, keywords, length, tone]);

  const isNote = kind === 'connect_note';

  const addKeyword = (kw) => {
    const v = (kw || '').trim();
    if (!v) return;
    if (keywords.includes(v)) return;
    setKeywords([...keywords, v]);
    setKwInput('');
  };

  const removeKeyword = (kw) => setKeywords(keywords.filter((k) => k !== kw));

  const generate = async (n = 1) => {
    if (!goal.trim()) {
      toast.error('Enter a message goal first');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/api/linkedin-msg/generate-template`,
        {
          kind,
          goal,
          keywords,
          audience,
          length,
          tone,
          variants: n,
        },
        { withCredentials: true },
      );
      if (!data?.templates?.length) {
        toast.error('No output from Qwen');
        return;
      }
      const first = data.templates[0];
      setLastMeta({
        model: data.model,
        duration_ms: data.duration_ms,
        chars: data.char_counts?.[0] || first.length,
        spam_score: data.spam_score,
        warnings: data.warnings || [],
        variants: data.templates,
        ts: Date.now(),
      });
      onGenerated?.(first, data);
      if ((data.warnings || []).length) {
        toast.warning(`Warnings: ${data.warnings.join(' · ')}`);
      } else {
        toast.success(`Generated in ${Math.round(data.duration_ms)}ms`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-white to-white overflow-hidden mb-3">
      <div className="px-3 py-2 bg-gradient-to-r from-purple-100/60 to-transparent border-b border-purple-100 flex items-center gap-2">
        <div className="bg-purple-600 rounded p-1">
          <WandSparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-xs font-semibold text-purple-900">
          Generate {isNote ? 'connect note' : 'template'} with AI
        </span>
        <span className="text-[10px] font-mono text-purple-700 bg-white border border-purple-200 rounded-full px-2 py-0.5">
          Ollama · qwen3.5:35b
        </span>
        <span className="ml-auto text-[10px] text-purple-700">DGX · 8× H200</span>
      </div>

      <div className="px-3 py-3 space-y-2.5">
        <div>
          <label className="text-[11px] font-medium text-gray-700 block mb-1">Message goal</label>
          <input
            type="text"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder={isNote
              ? 'Get them to accept the connection request'
              : 'Book a 15-minute intro call to explore AI-assisted hiring'}
            className="w-full h-9 px-3 border border-gray-200 rounded-md text-sm bg-white"
          />
        </div>

        <div>
          <label className="text-[11px] font-medium text-gray-700 block mb-1">Keywords / angles to hit</label>
          <div className="flex flex-wrap gap-1.5 items-center p-2 border border-gray-200 rounded-md bg-white min-h-[36px]">
            {keywords.map((kw) => (
              <span key={kw} className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-800 pl-2 pr-1 py-0.5 rounded-full">
                {kw}
                <button onClick={() => removeKeyword(kw)} className="hover:bg-purple-200 rounded-full p-0.5">
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <input
              value={kwInput}
              onChange={(e) => setKwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(kwInput); } }}
              onBlur={() => addKeyword(kwInput)}
              placeholder="+ add keyword, press Enter"
              className="flex-1 min-w-[120px] text-xs outline-none border-0 bg-transparent"
            />
          </div>
        </div>

        {audience?.length > 0 && (
          <div>
            <label className="text-[11px] font-medium text-gray-700 block mb-1">Audience</label>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {audience.map((a, i) => {
                const Icon = i === 0 ? Users : i === 1 ? Globe : Briefcase;
                return (
                  <span key={a} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-[#0A66C2] border border-blue-100">
                    <Icon className="w-3 h-3" /> {a}
                  </span>
                );
              })}
              <span className="text-[10px] text-gray-400 ml-1">auto-inferred from search URL</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 pt-1">
          {!isNote && (
            <div>
              <label className="text-[11px] font-medium text-gray-700 block mb-1">Length</label>
              <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                {['short', 'medium', 'long'].map((l) => (
                  <button
                    key={l}
                    onClick={() => setLength(l)}
                    className={`text-xs px-2.5 py-1 rounded capitalize ${length === l ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-[11px] font-medium text-gray-700 block mb-1">Tone</label>
            <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
              {['professional', 'friendly', 'direct', 'warm'].map((t) => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`text-xs px-2.5 py-1 rounded capitalize ${tone === t ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              disabled={generating}
              onClick={() => generate(3)}
              className="h-9 px-3 rounded-md text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Shuffle className="w-3.5 h-3.5" /> 3 variants
            </button>
            <button
              disabled={generating}
              onClick={() => generate(1)}
              className="h-9 px-4 rounded-md text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60"
              style={{ background: '#9333ea' }}
            >
              <WandSparkles className="w-3.5 h-3.5" />
              {generating ? 'Generating…' : 'Generate'}
            </button>
          </div>
        </div>

        {lastMeta && (
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-purple-100">
            <div className="text-[11px] text-purple-800 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              {lastMeta.chars} chars · spam {lastMeta.spam_score?.toFixed?.(2) ?? '0.00'}
              {lastMeta.warnings.length > 0 && <span className="text-amber-700"> · {lastMeta.warnings[0]}</span>}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => generate(1)}
                className="text-[11px] text-gray-500 hover:text-purple-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-purple-50"
              >
                <RefreshCw className="w-3 h-3" /> Regenerate
              </button>
              <button
                onClick={() => generate(3)}
                className="text-[11px] text-gray-500 hover:text-purple-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-purple-50"
              >
                <GitBranch className="w-3 h-3" /> Variants
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
